import { useTranslation } from 'react-i18next'

import {
  Checkbox,
  LightModeSemanticColors,
  Separator,
  Text,
  TextButton,
} from '@enechain/ecloud-designsystem'

import { AwaitingVoltageLegend } from '~/components/balance/AwaitingVoltageMark'
import { DateNav } from '~/components/balance/FilterCard/DateNav'
import { ToggleSwitch } from '~/components/balance/FilterCard/ToggleSwitch'
import { HelpIcon } from '~/components/common/HelpIcon'
import { AreaFilter } from '~/components/common/filters'
import {
  BALANCE_DEMAND_SERIES_COLORS,
  BALANCE_VOLTAGE_CLASS_COLORS,
} from '~/constants/chartPalette'
import { typography } from '~/constants/typography'
import type { SeriesKey } from '~/pages/BalanceDemand/constants'
import type { UseFilterStateReturn } from '~/pages/BalanceDemand/hooks/useFilterState'

type Props = {
  filter: UseFilterStateReturn
  /**
   * 表示中のエリアに低圧未着のコマがあるか。 ある時だけ「詳細」 の行の末尾に
   * 網掛けの見本を出す (design `balance/bg.html` の `#lv-pending-legend`)。
   */
  hasAwaitingVoltageKoma: boolean
}

type ToggleItem = {
  key: SeriesKey
  labelKey: string
  /** ON トラック色 (= チャート凡例色)。 省略 = 既定色 (accent-primary)。 */
  color?: string
}

// [design差異] design (`balance/bg.html` L68-69) は「基本表示」に太陽光計画値/
// 太陽光実績値の switch-item (data-series="solarPlan"/"solarActual") を今も
// 持つが、データソースが未確定・未配線 (#2002) のため #5282 で画面から削除した
// (doji-bg.md Discussion「太陽光計画値/実績値 系列の扱い」)。復活条件は #2002 の
// 解決とセット。design 側の改修は未着手 — 本注記が実装漏れ判定の起点。
const BASIC_TOGGLES: ToggleItem[] = [
  {
    key: 'demandPlan',
    labelKey: 'page:balanceDemand.filter.demandPlan',
    color: BALANCE_DEMAND_SERIES_COLORS.demandPlan,
  },
  {
    key: 'demandActual',
    labelKey: 'page:balanceDemand.filter.demandActual',
    color: BALANCE_DEMAND_SERIES_COLORS.demandActual,
  },
  { key: 'weather', labelKey: 'page:balanceDemand.filter.weather' },
]

const ANALYSIS_TOGGLES: ToggleItem[] = [
  {
    key: 'temperature',
    labelKey: 'page:balanceDemand.filter.temperature',
    color: BALANCE_DEMAND_SERIES_COLORS.temperature,
  },
  {
    key: 'precipitation',
    labelKey: 'page:balanceDemand.filter.precipitation',
    color: BALANCE_DEMAND_SERIES_COLORS.precipitation,
  },
  {
    key: 'procurementNet',
    labelKey: 'page:balanceDemand.filter.procurementNet',
    color: BALANCE_DEMAND_SERIES_COLORS.procurementNet,
  },
]

// 電圧階級別は「詳細」配下から独立した親トグルグループ (doji-bg.md AC-BDB-031)。
// 3 系列を 1 トグルでまとめて出し入れする形だとトラック色に単一の系列色を持たせられず、
// トグル自体が凡例を兼ねられないため、階級ごとに分けて凡例色を持たせる。
const VOLTAGE_TOGGLES: ToggleItem[] = [
  {
    key: 'voltageExtraHigh',
    labelKey: 'page:balanceDemand.filter.voltageExtraHigh',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageExtraHigh,
  },
  {
    key: 'voltageHigh',
    labelKey: 'page:balanceDemand.filter.voltageHigh',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageHigh,
  },
  {
    key: 'voltageLow',
    labelKey: 'page:balanceDemand.filter.voltageLow',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageLow,
  },
]

const DETAIL_TOGGLES: ToggleItem[] = [
  {
    key: 'demandActualCorrected',
    labelKey: 'page:balanceDemand.filter.demandActualCorrected',
    color: BALANCE_DEMAND_SERIES_COLORS.demandActualCorrected,
  },
  {
    key: 'dewPoint',
    labelKey: 'page:balanceDemand.filter.dewPoint',
    color: BALANCE_DEMAND_SERIES_COLORS.dewPoint,
  },
]

export const FilterCard = ({
  filter,
  hasAwaitingVoltageKoma,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  }

  const labelStyle: React.CSSProperties = {
    // design balance.css `.filter-card .form-label` = md (14px) / 600 (2026-08-06 実測)。
    // 素の DOM style なので役割 typography の raw CSSProperties を使う
    // (= chakra の textStyle token は解決されない)。
    ...typography.label,
    // design balance.css `.filter-row > .form-label` = inline-flex / gap 4px
    // (見出し + 補足 (?) アイコンを横並びにする列)。
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    // 104px は列の下限。 design は同 CSS の `html[lang="en"]` 分岐 (balance.css L34-36) で
    // 訳語が収まらない時だけ `width:auto; min-width:104px` に逃がしており、 checkbox +
    // 見出し + 補足アイコンが載る行はその条件に当たる (nowrap のままだと右隣のトグルに
    // 重なる)。 アイコンを持たない行は 104px のままなので列の縦揃えは保たれる。
    minWidth: '104px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  }

  const renderToggle = (
    item: ToggleItem,
    isDisabled: boolean,
  ): React.ReactElement => (
    <ToggleSwitch
      key={item.key}
      isDisabled={isDisabled}
      isOn={filter.seriesVisibility[item.key]}
      label={t(item.labelKey)}
      onColor={item.color}
      onToggle={() => filter.toggleSeries(item.key)}
    />
  )

  return (
    <div
      style={{
        background: LightModeSemanticColors.Surface.primary.value,
        // design `css/balance.css:10` の `.filter-card { border:1px solid
        // var(--border-low) }`。 実装は 1 段濃い midEmphasis (#d9d9d9) で、
        // 4 画面とも同じズレ方をしていた (issue #7064)。
        border: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '2px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
        <DateNav
          displayDate={filter.displayDate}
          labelKeyPrefix="page:balanceDemand.filter"
          layout="vertical"
          onDateChange={filter.setDisplayDate}
          onToday={filter.goToToday}
        />

        <Separator orientation="vertical" />

        {/* Right section: toggles */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Basic series */}
          <div style={rowStyle}>
            <span style={labelStyle}>
              {t('page:balanceDemand.filter.basicSeries')}
            </span>
            {BASIC_TOGGLES.map((item) => renderToggle(item, false))}
          </div>

          <Separator />

          {/* Voltage class (基本表示の次・分析補助より前 = design bg.html #filter-volt) */}
          <div style={rowStyle}>
            <span style={labelStyle}>
              <Checkbox
                checked={filter.isVoltageEnabled}
                onCheckedChange={filter.toggleVoltage}
              >
                <Text as="span" textStyle="label">
                  {t('page:balanceDemand.filter.voltageClass')}
                </Text>
              </Checkbox>
              <HelpIcon
                ariaLabel={t('page:balanceDemand.filter.groupHelp', {
                  group: t('page:balanceDemand.filter.voltageClass'),
                })}
                content={t('page:balanceDemand.filter.voltageClassTooltip')}
              />
            </span>
            {VOLTAGE_TOGGLES.map((item) =>
              renderToggle(item, !filter.isVoltageEnabled),
            )}
          </div>

          {/* Analysis */}
          <div style={rowStyle}>
            <span style={labelStyle}>
              <Checkbox
                checked={filter.isAnalysisEnabled}
                onCheckedChange={filter.toggleAnalysis}
              >
                {/* この checkbox は「値」ではなく行の見出し (design bg.html は
                    `.form-label` 内に置いて 14px/600 で描く。2026-08-06 実測)。
                    DS checkbox の label recipe は weight 400 固定なので明示する。 */}
                <Text as="span" textStyle="label">
                  {t('page:balanceDemand.filter.analysis')}
                </Text>
              </Checkbox>
              <HelpIcon
                ariaLabel={t('page:balanceDemand.filter.groupHelp', {
                  group: t('page:balanceDemand.filter.analysis'),
                })}
                content={t('page:balanceDemand.filter.analysisTooltip')}
              />
            </span>
            {ANALYSIS_TOGGLES.map((item) =>
              renderToggle(item, !filter.isAnalysisEnabled),
            )}
          </div>

          {/* Detail */}
          <div style={rowStyle}>
            <span style={labelStyle}>
              <Checkbox
                checked={filter.isDetailEnabled}
                onCheckedChange={filter.toggleDetail}
              >
                <Text as="span" textStyle="label">
                  {t('page:balanceDemand.filter.detail')}
                </Text>
              </Checkbox>
              <HelpIcon
                ariaLabel={t('page:balanceDemand.filter.groupHelp', {
                  group: t('page:balanceDemand.filter.detail'),
                })}
                content={t('page:balanceDemand.filter.detailTooltip')}
              />
            </span>
            {DETAIL_TOGGLES.map((item) =>
              renderToggle(item, !filter.isDetailEnabled),
            )}
            {hasAwaitingVoltageKoma && <AwaitingVoltageLegend />}
          </div>

          <Separator />

          {/* Area checkboxes */}
          <div style={rowStyle}>
            <span style={{ ...labelStyle, marginBottom: 0 }}>
              {t('page:balanceDemand.filter.displayArea')}
            </span>
            <AreaFilter
              allLabel={t('page:balanceDemand.filter.allAreas')}
              areaCodes={filter.selectableAreaCodes}
              onChange={(next) => filter.setVisibleAreaCodes(next)}
              value={filter.visibleAreaCodes}
              variant="checkbox"
            />
          </div>
        </div>

        {/* フィルタ群のクリア。 このカードの中で指定したもの (系列トグル /
            電圧階級 / 分析補助 / 詳細 / エリア) だけを初期値へ戻す。 表示日は
            戻さない (理由は `useFilterState.clearFilters` の JSDoc)。

            [design差異] 8080 (`balance/bg.html` L135 `#bg-layout-reset-btn`) は
            同じ位置の「クリア」1 つで**フィルタとグラフ配置を両方**戻す。
            2026-09-07 のデザイナー判断で 2 つに分け、グラフ配置は
            チャートの上・右端の「レイアウトリセット」が持つ。 ボタンの見た目と
            ラベルは同 repo の相対取引一覧 (`SupplyBilateral/components/FilterBar`)
            の「クリア」に揃えた。 8080 と spec の更新は issue で PdM へ回す。 */}
        <TextButton
          flexShrink={0}
          alignSelf="flex-start"
          onClick={filter.clearFilters}
          size="sm"
        >
          {t('page:balanceDemand.filter.clear')}
        </TextButton>
      </div>
    </div>
  )
}
